// Entregas: cola de almacén (asignar repartidor + fecha) y "mis entregas" del repartidor.
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { listUsers } from '@/services/users'
import {
  STATUS_CLASS,
  STATUS_LABEL,
  WAREHOUSES,
  assignDelivery,
  formatMoney,
  getDeliverySummary,
  listDeliveries,
  periodFilters,
  syncDeliveries,
} from '@/services/deliveries'
import type { Delivery } from '@/types'

const today = () => new Date().toISOString().slice(0, 10)

export default function DeliveryList() {
  const { user } = useAuth()
  const isDriver = user?.role === 'repartidor'
  // Un gerente con almacén asignado solo ve el suyo: el backend lo fuerza, así que
  // aquí se muestra como dato fijo en vez de un selector que no haría nada.
  const fixedWarehouse = user?.role === 'almacen' ? user?.warehouse ?? null : null
  const qc = useQueryClient()

  const [warehouse, setWarehouse] = useState('')
  const [status, setStatus] = useState(isDriver ? '' : 'pendiente')
  const [q, setQ] = useState('')
  // Dos cosas que NO son reparto en camión y por eso se ocultan salvo que se pidan:
  // ECOM (MercadoLibre/Amazon/…) y las MUESTRAS a asesores, que además nunca se facturan
  // y por eso su entrega vive abierta en SAP para siempre. Al repartidor no se le filtra.
  const [showEcom, setShowEcom] = useState(false)
  const [showSamples, setShowSamples] = useState(false)
  // La cola arrastra años de documentos. OJO: "cerrada en SAP" NO quiere decir "entregada":
  // aquí se FACTURA ANTES de cargar el camión, así que lo recién facturado es justo lo que
  // sale hoy. Por eso se navega por FECHA, no por el estatus de SAP, y se abre en la ventana
  // reciente. Al repartidor no se le filtra nada: lo suyo ya está asignado.
  const [period, setPeriod] = useState(isDriver ? '' : '7')
  const [assigning, setAssigning] = useState<Delivery | null>(null)

  // Sin el periodo: el resumen cuenta justo lo que se vería al elegir cada opción.
  const baseFilters = {
    ...(warehouse ? { warehouse } : {}),
    ...(status ? { status } : {}),
    ...(q ? { q } : {}),
    ...(isDriver || showEcom || warehouse ? {} : { ecom: false }),
    ...(isDriver || showSamples ? {} : { sample: false }),
  }
  const { data: summary } = useQuery({
    queryKey: ['deliveries', 'summary', baseFilters],
    queryFn: () => getDeliverySummary(baseFilters),
    enabled: !isDriver,
  })
  const staleDays = summary?.stale_days ?? 30
  const filters = { ...baseFilters, ...periodFilters(period, staleDays), size: 50 }

  const { data, isLoading } = useQuery({
    queryKey: ['deliveries', filters],
    queryFn: () => listDeliveries(filters),
  })

  const sync = useMutation({
    mutationFn: syncDeliveries,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deliveries'] }),
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title={isDriver ? 'Mis entregas' : 'Entregas'}
        subtitle={
          isDriver
            ? 'Las entregas asignadas a ti'
            : 'Cola sincronizada desde SAP · asigna repartidor y fecha'
        }
        actions={
          !isDriver && (
            <Button variant="secondary" onClick={() => sync.mutate()} disabled={sync.isPending}>
              {sync.isPending ? 'Sincronizando…' : '🔄 Sincronizar SAP'}
            </Button>
          )
        }
      />

      {sync.isSuccess && (
        <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">
          Sincronizado: {sync.data.upserted} entregas desde SAP.
        </p>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar cliente o # documento…"
          className="min-w-56 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        {!isDriver && (fixedWarehouse ? (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700">
            🏬 Almacén {fixedWarehouse}
          </span>
        ) : (
          <select
            value={warehouse}
            onChange={(e) => setWarehouse(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Todos los almacenes</option>
            {WAREHOUSES.map((w) => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
        ))}
        {!isDriver && (
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="7">Últimos 7 días</option>
            <option value="30">Últimos 30 días</option>
            <option value="90">Últimos 90 días</option>
            <option value="rezago">
              Rezagadas: sin facturar +{staleDays} días
              {summary ? ` (${summary.stale_open})` : ''}
            </option>
            <option value="">Todas</option>
            {summary?.years.map((y) => (
              <option key={y.year} value={y.year}>{y.year} ({y.count})</option>
            ))}
          </select>
        )}
        {!isDriver && !warehouse && (
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showEcom}
              onChange={(e) => setShowEcom(e.target.checked)}
              className="h-4 w-4 accent-ds-600"
            />
            Mostrar e-commerce
          </label>
        )}
        {!isDriver && (
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showSamples}
              onChange={(e) => setShowSamples(e.target.checked)}
              className="h-4 w-4 accent-ds-600"
            />
            Mostrar muestras{summary ? ` (${summary.samples})` : ''}
          </label>
        )}
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendientes de asignar</option>
          <option value="asignada">Asignadas</option>
          <option value="en_ruta,en_sitio">En proceso</option>
          <option value="entregada">Entregadas</option>
          <option value="no_entregada">No entregadas</option>
        </select>
      </div>

      {!isDriver && period !== 'rezago' && !!summary?.stale_open && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-amber-200">
          <span>
            ⚠️ {summary.stale_open} entrega(s) de hace más de {staleDays} días siguen ABIERTAS en
            SAP y no son muestras: nunca se facturaron. O se entregaron sin registrar, o hay que
            descartarlas.
          </span>
          <button
            onClick={() => setPeriod('rezago')}
            className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold hover:bg-amber-300"
          >
            Ver rezagadas
          </button>
        </div>
      )}

      {isLoading ? (
        <p className="p-8 text-center text-gray-500">Cargando…</p>
      ) : !data?.items.length ? (
        <p className="rounded-xl bg-white p-8 text-center text-gray-500 ring-1 ring-black/5">
          No hay entregas con estos filtros.
        </p>
      ) : (
        <>
          <p className="text-xs text-gray-500">
            {data.total} entrega(s)
            {period === 'rezago'
              ? ` · sin facturar desde hace más de ${staleDays} días`
              : /^[0-9]{4}$/.test(period)
                ? ` de ${period}`
                : period
                  ? ` de los últimos ${period} días`
                  : ''}
          </p>
          <div className="space-y-2">
            {data.items.map((d) => (
              <DeliveryRow
                key={d.id}
                d={d}
                canAssign={!isDriver}
                onAssign={() => setAssigning(d)}
              />
            ))}
          </div>
        </>
      )}

      {assigning && (
        <AssignModal delivery={assigning} onClose={() => setAssigning(null)} />
      )}
    </div>
  )
}

function DeliveryRow({
  d,
  canAssign,
  onAssign,
}: {
  d: Delivery
  canAssign: boolean
  onAssign: () => void
}) {
  return (
    <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Link to={`/entregas/${d.id}`} className="font-semibold text-ds-700 hover:underline">
              #{d.sap_doc_num}
            </Link>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLASS[d.status]}`}>
              {STATUS_LABEL[d.status]}
            </span>
            {d.is_sample && (
              <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                Muestra
              </span>
            )}
            {d.reschedule_count > 0 && (
              <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs text-orange-800">
                reprogramada ×{d.reschedule_count}
              </span>
            )}
          </div>
          <p className="mt-1 truncate font-medium text-gray-900">{d.card_name ?? '—'}</p>
          <p className="truncate text-xs text-gray-500">{d.ship_address ?? 'Sin dirección'}</p>
        </div>

        <div className="text-right text-sm">
          <p className="font-semibold text-gray-900">{formatMoney(d.total_doc, d.currency)}</p>
          <p className="text-xs text-gray-500">{d.warehouse ?? '—'}</p>
          <p className="text-xs text-gray-500">
            {d.scheduled_date
              ? `Entrega: ${d.scheduled_date}`
              : d.sap_ship_date
                ? `Comprometida: ${d.sap_ship_date}`
                : 'Sin fecha comprometida'}
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
        <p className="text-xs text-gray-500">
          {d.driver ? `Repartidor: ${d.driver.name}` : 'Sin repartidor'}
          {d.lines?.length ? ` · ${d.lines.length} artículo(s)` : ''}
        </p>
        <div className="flex gap-2">
          <Link to={`/entregas/${d.id}`}>
            <Button size="sm" variant="secondary">Ver</Button>
          </Link>
          {canAssign && (
            <Button size="sm" onClick={onAssign}>
              {d.driver ? 'Reasignar' : 'Asignar'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function AssignModal({ delivery, onClose }: { delivery: Delivery; onClose: () => void }) {
  const qc = useQueryClient()
  const [driverId, setDriverId] = useState(delivery.driver_id ?? '')
  const [date, setDate] = useState(
    delivery.scheduled_date ?? delivery.sap_ship_date ?? delivery.sap_due_date ?? today(),
  )

  const { data: users } = useQuery({ queryKey: ['users'], queryFn: () => listUsers(1, 200) })
  const drivers = (users?.items ?? []).filter((u) => u.role === 'repartidor' && u.is_active)

  const save = useMutation({
    mutationFn: () => assignDelivery(delivery.id, { driver_id: driverId, scheduled_date: date }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deliveries'] })
      onClose()
    },
  })

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-gray-900">Asignar entrega #{delivery.sap_doc_num}</h2>
        <p className="mt-1 text-sm text-gray-500">{delivery.card_name}</p>

        <label className="mt-4 block text-sm font-medium text-gray-700">Repartidor</label>
        <select
          value={driverId}
          onChange={(e) => setDriverId(e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">Selecciona…</option>
          {drivers.map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
        {!drivers.length && (
          <p className="mt-1 text-xs text-orange-700">
            No hay repartidores activos. Da de alta usuarios con rol “repartidor”.
          </p>
        )}

        <label className="mt-4 block text-sm font-medium text-gray-700">Fecha de entrega</label>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />

        {save.isError && (
          <p className="mt-3 text-sm text-red-700">
            {(save.error as { message?: string })?.message ?? 'No se pudo asignar.'}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => save.mutate()} disabled={!driverId || !date || save.isPending}>
            {save.isPending ? 'Guardando…' : 'Asignar'}
          </Button>
        </div>
      </div>
    </div>
  )
}
